import { logger } from '@inboundx/shared/platform';

import { supabase } from '@/lib/supabase';

/** A member of the current account admin's client account(s). */
export interface AccountMember {
    backoffice_user_id: string;
    /** Whether the member has signed in (linked to an auth user) — not the auth UUID. */
    is_linked: boolean;
    email: string;
    display_name: string | null;
    is_active: boolean;
    /** Domains this member is granted, within the caller's administered accounts. */
    domains: string[];
    /** Accounts (client_ids) where this member is themselves an account admin. */
    admin_client_ids: string[];
}

/** A client account the current user administers. */
export interface AccountInfo {
    client_id: string;
    client_name: string;
    /** Non-alias domains belonging to the account. */
    domains: string[];
}

// The account_* RPCs ship in the IX-3872 migration and are not (yet) in the
// generated Supabase types. Call them through a minimal untyped RPC signature;
// authorization and validation live in the SECURITY DEFINER functions.
// NOTE: call `supabase.rpc` as a method (don't detach it) so `this` stays bound —
// a detached reference throws "Cannot read properties of undefined (reading 'rest')".
type RpcResult<T> = { data: T | null; error: { message: string } | null };
type UntypedSupabase = {
    rpc: (fn: string, args?: Record<string, unknown>) => PromiseLike<RpcResult<unknown>>;
};
const rpc = (fn: string, args?: Record<string, unknown>): PromiseLike<RpcResult<unknown>> =>
    (supabase as unknown as UntypedSupabase).rpc(fn, args);

/** Map a raised Postgres error message to client-facing copy. */
function friendlyError(message: string, fallback: string): string {
    if (message.includes('last_admin')) {
        return 'This account must keep at least one admin. Promote someone else first.';
    }
    if (message.includes('target_is_staff')) {
        return 'This user is a Rose staff member and cannot be managed here.';
    }
    if (message.includes('target_inactive_foreign')) {
        return 'This inactive user belongs to another account. Ask Rose support to reactivate them.';
    }
    if (message.includes('not_a_member')) {
        return 'Add this person to the account before making them an admin.';
    }
    if (message.includes('forbidden_domain') || message.includes('forbidden')) {
        return 'You can only manage members of your own account.';
    }
    if (message.includes('invalid_email')) return 'Please enter a valid email address.';
    if (message.includes('no_domains')) return 'Select at least one domain for this member.';
    return fallback;
}

/** Members of the account owning `domain` (IX-4441 — scoped to the selected client). */
export async function fetchAccountMembers(domain: string): Promise<AccountMember[]> {
    const { data, error } = await rpc('account_list_members', { p_domain: domain });
    if (error) {
        logger.error('Error fetching account members:', error);
        throw new Error('Failed to load team members');
    }
    return (data as AccountMember[] | null) ?? [];
}

/**
 * Every domain of every account the caller is an account admin of. Used to decide
 * per-client whether the Team surface is theirs to manage — `is_account_admin` on
 * its own only says "admin of *some* account".
 */
export async function fetchAdministeredDomains(): Promise<string[]> {
    const { data, error } = await rpc('account_my_accounts');
    if (error) {
        logger.error('Error fetching administered accounts:', error);
        return [];
    }
    return ((data as AccountInfo[] | null) ?? []).flatMap((a) => a.domains);
}

/** The account owning `domain`, or null when the caller may not administer it. */
export async function fetchDomainAccount(domain: string): Promise<AccountInfo | null> {
    const { data, error } = await rpc('account_my_accounts', { p_domain: domain });
    if (error) {
        logger.error('Error fetching account:', error);
        throw new Error('Failed to load account');
    }
    return (data as AccountInfo[] | null)?.[0] ?? null;
}

export async function addAccountMember(input: {
    email: string;
    display_name?: string;
    domains: string[];
}): Promise<void> {
    const { error } = await rpc('account_add_member', {
        p_email: input.email.trim(),
        p_display_name: input.display_name?.trim() || null,
        p_domains: input.domains,
    });
    if (error) {
        logger.error('Error adding account member:', error);
        throw new Error(friendlyError(error.message, 'Failed to add member'));
    }
}

/**
 * Replace the member's grants *within `clientId`* — grants in the caller's other
 * accounts are untouched, so editing a member under one client cannot strip
 * their access to another (IX-4441).
 */
export async function setMemberDomains(
    memberId: string,
    domains: string[],
    clientId: string
): Promise<void> {
    const { error } = await rpc('account_set_member_domains', {
        p_member_id: memberId,
        p_domains: domains,
        p_client_id: clientId,
    });
    if (error) {
        logger.error('Error updating member domains:', error);
        throw new Error(friendlyError(error.message, 'Failed to update domains'));
    }
}

export async function setMemberAdmin(
    memberId: string,
    clientId: string,
    isAdmin: boolean
): Promise<void> {
    const { error } = await rpc('account_set_member_admin', {
        p_member_id: memberId,
        p_client_id: clientId,
        p_is_admin: isAdmin,
    });
    if (error) {
        logger.error('Error updating member admin status:', error);
        throw new Error(friendlyError(error.message, 'Failed to update admin status'));
    }
}

/** Remove the member from `clientId` only — never from the caller's other accounts. */
export async function removeAccountMember(
    memberId: string,
    hardDelete: boolean,
    clientId: string
): Promise<void> {
    const { error } = await rpc('account_remove_member', {
        p_member_id: memberId,
        p_hard_delete: hardDelete,
        p_client_id: clientId,
    });
    if (error) {
        logger.error('Error removing account member:', error);
        throw new Error(friendlyError(error.message, 'Failed to remove member'));
    }
}
